import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
} from 'antd';
import type { RollbackDrainStatus, UnknownOutcomeBlocker } from '../types.js';

export function RollbackDrainSnapshot({
  status,
  loading,
  onReview,
}: {
  status: RollbackDrainStatus;
  loading: boolean;
  onReview: (jobId: string) => void;
}) {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        type={status.canSwitchToLocal ? 'success' : 'warning'}
        showIcon
        message={
          status.canSwitchToLocal
            ? 'Drain complete: product may switch to local mode'
            : 'Do not switch the product to local mode'
        }
        description={
          status.canSwitchToLocal
            ? 'Central dispatch is disabled and no in-flight jobs, open realtime sessions, or unreviewed unknown outcomes remain.'
            : 'Set every selected central profile to LOCAL / 0, drain jobs and sessions, and review every unknown outcome first.'
        }
      />
      {status.blockers.truncated ? (
        <Alert
          type="error"
          showIcon
          message="Blocker tables are truncated at 100 rows; use the counts as authoritative."
        />
      ) : null}
      <Row gutter={[16, 16]}>
        <Metric title="Dispatch disabled" value={status.dispatchDisabled} />
        <Metric title="In-flight jobs" value={status.counts.inFlightJobs} />
        <Metric
          title="Open realtime"
          value={status.counts.openRealtimeSessions}
        />
        <Metric
          title="Unreviewed unknowns"
          value={status.counts.unreviewedUnknownOutcomes}
        />
      </Row>
      <ProfileTable status={status} loading={loading} />
      <JobBlockers status={status} loading={loading} />
      <RealtimeBlockers status={status} loading={loading} />
      <UnknownOutcomeBlockers
        status={status}
        loading={loading}
        onReview={onReview}
      />
    </Space>
  );
}

function Metric({ title, value }: { title: string; value: boolean | number }) {
  return (
    <Col xs={24} sm={12} xl={6}>
      <Card>
        <Statistic
          title={title}
          value={typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}
          {...(typeof value === 'boolean'
            ? { valueStyle: { color: value ? '#389e0d' : '#cf1322' } }
            : {})}
        />
      </Card>
    </Col>
  );
}

function ProfileTable({
  status,
  loading,
}: {
  status: RollbackDrainStatus;
  loading: boolean;
}) {
  return (
    <Card title="Active central profiles">
      <Table
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={status.profiles}
        columns={[
          { title: 'Operation', dataIndex: 'key' },
          { title: 'Version', dataIndex: 'version' },
          { title: 'Mode', dataIndex: 'executionMode' },
          {
            title: 'Percentage',
            render: (_, row) => `${row.canaryPercentage}%`,
          },
        ]}
      />
    </Card>
  );
}

function JobBlockers({
  status,
  loading,
}: {
  status: RollbackDrainStatus;
  loading: boolean;
}) {
  return (
    <Card title={`In-flight jobs (${status.counts.inFlightJobs})`}>
      <Table
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={status.blockers.inFlightJobs}
        columns={[
          { title: 'Job', dataIndex: 'id' },
          { title: 'Operation', render: (_, row) => row.operationProfile.key },
          { title: 'Status', dataIndex: 'status' },
          { title: 'Created', dataIndex: 'createdAt', render: timestamp },
          {
            title: 'Lease expires',
            dataIndex: 'executionLeaseExpiresAt',
            render: timestamp,
          },
        ]}
      />
    </Card>
  );
}

function RealtimeBlockers({
  status,
  loading,
}: {
  status: RollbackDrainStatus;
  loading: boolean;
}) {
  return (
    <Card
      title={`Open realtime sessions (${status.counts.openRealtimeSessions})`}
    >
      <Table
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={status.blockers.openRealtimeSessions}
        columns={[
          { title: 'Session', dataIndex: 'id' },
          { title: 'Job', dataIndex: 'jobId' },
          { title: 'Operation', dataIndex: 'operationKey' },
          { title: 'Status', dataIndex: 'status' },
          { title: 'Expires', dataIndex: 'expiresAt', render: timestamp },
        ]}
      />
    </Card>
  );
}

function UnknownOutcomeBlockers({
  status,
  loading,
  onReview,
}: {
  status: RollbackDrainStatus;
  loading: boolean;
  onReview: (jobId: string) => void;
}) {
  return (
    <Card
      title={`Unreviewed unknown outcomes (${status.counts.unreviewedUnknownOutcomes})`}
      extra={
        <Tag color="blue">{status.counts.reviewedUnknownOutcomes} reviewed</Tag>
      }
    >
      <Table<UnknownOutcomeBlocker>
        rowKey="id"
        loading={loading}
        pagination={false}
        dataSource={status.blockers.unreviewedUnknownOutcomes}
        columns={[
          { title: 'Job', dataIndex: 'id' },
          { title: 'Operation', render: (_, row) => row.operationProfile.key },
          {
            title: 'Provider',
            dataIndex: 'selectedProvider',
            render: fallback,
          },
          { title: 'Model', dataIndex: 'selectedModel', render: fallback },
          {
            title: 'Provider request IDs',
            render: (_, row) => providerRequestIds(row),
          },
          {
            title: 'Action',
            render: (_, row) => (
              <Button danger size="small" onClick={() => onReview(row.id)}>
                Review
              </Button>
            ),
          },
        ]}
      />
    </Card>
  );
}

function providerRequestIds(row: UnknownOutcomeBlocker): string {
  return (
    row.attempts
      .map((attempt) => attempt.providerRequestId)
      .filter(Boolean)
      .join(', ') || '—'
  );
}

function timestamp(value: unknown): string {
  return typeof value === 'string' ? new Date(value).toLocaleString() : '—';
}

function fallback(value: unknown): string {
  return typeof value === 'string' && value ? value : '—';
}
